import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { WorkoutBlockForm } from "@/components/athlete-profile/workouts/form";
import { ResourceError } from "@/components/ui/resource-error";
import { useWorkoutBlock } from "@/queries/use-workout-blocks";
import {
  useAthleteNameBreadcrumb,
  useEntityNameBreadcrumb,
} from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { guardWorkoutBlockId } from "@/utils/route-guards";

export const Route = createFileRoute(
  "/(protected)/athletes/$athleteId/workout-blocks/$workoutBlockId/edit/",
)({
  beforeLoad: ({ params }) => guardWorkoutBlockId(params),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { athleteId, workoutBlockId } = Route.useParams();
  const numericAthleteId = Number(athleteId);
  const numericWorkoutBlockId = Number(workoutBlockId);

  const { data: workoutBlock, error } = useWorkoutBlock(
    numericAthleteId,
    numericWorkoutBlockId,
  );

  useAthleteNameBreadcrumb(numericAthleteId);
  useEntityNameBreadcrumb(workoutBlock?.name);

  const navigateToView = () =>
    navigate({
      to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_DETAIL,
      params: { athleteId, workoutBlockId },
      replace: true,
    });

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>
        {workoutBlock?.name ?? t("pages:profile.workouts.form.editTitle")}
      </MobilePageTitle>
      <PageContainer
        className="flex min-h-0 flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        {error ? (
          <ResourceError
            error={error}
            notFoundMessage={t("pages:profile.workouts.errors.notFound")}
            errorMessage={t("pages:profile.workouts.errors.loadingFailed")}
            className="m-6"
          />
        ) : (
          <ScrollableContainer>
            <div className="px-6 pt-6 pb-10 xl:px-12">
              <WorkoutBlockForm
                athleteId={numericAthleteId}
                workoutBlockId={numericWorkoutBlockId}
                onCancel={navigateToView}
                onAddPlan={() =>
                  navigate({
                    to: PROTECTED_ROUTES.ATHLETE_WORKOUT_PLAN_NEW,
                    params: { athleteId },
                    search: { workoutBlockId: numericWorkoutBlockId },
                  })
                }
              />
            </div>
          </ScrollableContainer>
        )}
      </PageContainer>
    </div>
  );
}
